import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Copy, Check, Mail, MessageCircle, QrCode, X } from "lucide-react";

type Props = { url?: string; testIdPrefix?: string };

/**
 * Wiederverwendbare Share-Karte (Phase 3: Objekt-Detailseiten). Für Phase 1
 * eingebunden, aber nur intern verwendbar. QR-Code wird lokal via `qrcode`-npm
 * generiert — keine externen Requests.
 */
export default function ShareCard({
    url,
    testIdPrefix = "share",
}: Props) {
    const [copied, setCopied] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState<string>("");
    const currentUrl =
        url ??
        (typeof window !== "undefined" ? window.location.href : "");
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        if (!qrOpen) return;
        QRCode.toDataURL(currentUrl, {
            margin: 2,
            width: 320,
            color: { dark: "#0E1726", light: "#FAF8F3" },
        })
            .then(setQrDataUrl)
            .catch(() => setQrDataUrl(""));
    }, [qrOpen, currentUrl]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(currentUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            /* ignore */
        }
    };

    const mail = `mailto:?subject=${encodeURIComponent("HypeFly · 360°-Immobiliendokumentation")}&body=${encodeURIComponent(currentUrl)}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(currentUrl)}`;

    return (
        <div
            data-testid={`${testIdPrefix}-card`}
            className="p-5 rounded"
            style={{
                background: "hsl(var(--hf-surface))",
                border: "1px solid hsl(var(--hf-line))",
            }}
        >
            <div className="hf-eyebrow">Diese Seite teilen</div>
            <div className="mt-3 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={copy}
                    className="hf-btn hf-btn-ghost"
                    data-testid={`${testIdPrefix}-copy`}
                >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    <span>{copied ? "Link kopiert" : "Link kopieren"}</span>
                </button>
                <a
                    href={mail}
                    className="hf-btn hf-btn-ghost"
                    data-testid={`${testIdPrefix}-mail`}
                >
                    <Mail size={14} />
                    <span>E-Mail</span>
                </a>
                <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hf-btn hf-btn-ghost"
                    data-testid={`${testIdPrefix}-whatsapp`}
                >
                    <MessageCircle size={14} />
                    <span>WhatsApp</span>
                </a>
                <button
                    type="button"
                    onClick={() => setQrOpen(true)}
                    className="hf-btn hf-btn-ghost"
                    data-testid={`${testIdPrefix}-qr`}
                >
                    <QrCode size={14} />
                    <span>QR-Code</span>
                </button>
            </div>
            {qrOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center px-6"
                    style={{ background: "rgba(14,23,38,0.65)" }}
                    role="dialog"
                    aria-modal="true"
                    data-testid={`${testIdPrefix}-qr-modal`}
                >
                    <div
                        className="max-w-sm w-full p-6 rounded"
                        style={{
                            background: "hsl(var(--hf-bg))",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                    >
                        <div className="flex items-start justify-between">
                            <div className="hf-eyebrow">QR-Code</div>
                            <button
                                type="button"
                                onClick={() => setQrOpen(false)}
                                aria-label="Schließen"
                                className="w-9 h-9 inline-flex items-center justify-center border border-hf-line rounded-sm"
                                data-testid={`${testIdPrefix}-qr-close`}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {qrDataUrl && (
                            <img
                                src={qrDataUrl}
                                width={320}
                                height={320}
                                alt="QR-Code zu dieser Seite"
                                className="mx-auto mt-4"
                            />
                        )}
                        <p className="mt-4 text-xs text-hf-muted break-all">
                            {currentUrl}
                        </p>
                    </div>
                    {/* fallback canvas ref (unused, kept for extension) */}
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>
            )}
        </div>
    );
}
